import _defineProperty from "@babel/runtime/helpers/defineProperty";
import AnalyticsCrossProductInteractionClient, { URL_QUERY_PARAM_INTERACTION_SESSION, validProducts } from '@atlassiansox/analytics-cross-product-interaction-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { perimeterType } from './analyticsWebTypes';
import urlParamExtractor from './urlUtils';
const isCrossProductInteractionAttribute = (_, paramName) => {
  return paramName === URL_QUERY_PARAM_INTERACTION_SESSION;
};
/**
 * This class initialises the Analytics Cross Product Interaction Client, which keeps track of the
 * interactionSessionId we use to track cross-product user journeys.
 *
 * It parses URL query parameters for any previous interaction session attributes.
 * If none exist, the Analytics Cross Product Interaction Client will generate a new
 * interactionSessionId, and the class will expose a method for retrieving this from Session Storage
 *
 * @param sendEventCallback: Expects a callback function that can send an analytics event
 */
export default class CrossProductInteractionTracking {
  constructor(options) {
    _defineProperty(this, "validProduct", false);
    this.isEnabled = fg('analytics_cross_product_interaction_tracking') && options.perimeter !== perimeterType.FEDRAMP_MODERATE;
    this.urlValidation = {
      result: true,
      interactionSessionBlob: ''
    };
    this.sendEventCallback = options.sendEventCallback;
    if (this.isEnabled) {
      // Validate product
      //
      // Various UI components in the page might initialize their own AWC instances but some of those
      // instances might be associated with subordinate products that are of no interest to XPC tracking. We
      // want to avoid XPC meta analytics being fired against uninteresting products.
      //
      // TODO - this is currently the best way we can try and guarantee that the most relevant product's AWC
      // is passing in the options. Down the line we ideally want the relevant products themselves to opt-in
      // to initialising cross product tracking via a flag in their AWC init options.
      this.validProduct = validProducts.includes(options.product);
      let bridge;
      let interactionSessionId;
      let interactionSource;
      let interactionSessionOptions;
      if (this.validProduct) {
        // Only parse URL parameters if we are in a valid product
        const crossProductUrlPramater = urlParamExtractor(isCrossProductInteractionAttribute);
        const interactionSessionBlob = URL_QUERY_PARAM_INTERACTION_SESSION in crossProductUrlPramater ? crossProductUrlPramater[URL_QUERY_PARAM_INTERACTION_SESSION] : null;
        if (interactionSessionBlob) {
          try {
            const interactionSession = JSON.parse(atob(interactionSessionBlob));
            if (interactionSession.bridge && interactionSession.id && interactionSession.source) {
              // All 3 fields must be present from a valid query param
              bridge = interactionSession.bridge;
              interactionSessionId = interactionSession.id;
              interactionSource = interactionSession.source;
            } else {
              this.urlValidation.result = false;
              this.urlValidation.interactionSessionBlob = interactionSessionBlob;
            }
          } catch (_unused) {
            // Gracefully fail through if parameter has been corrupted
            this.urlValidation.result = false;
            this.urlValidation.interactionSessionBlob = interactionSessionBlob;
          }
        }
        interactionSessionOptions = {
          analyticsEventCallback: this.sendEventCallback,
          persistedSessionId: interactionSessionId
        };
      }

      // Initialise interaction session client with parsed values if they exist
      this.analyticsCrossProductInteractionClient = new AnalyticsCrossProductInteractionClient(interactionSessionOptions, bridge, interactionSource);
    }
  }
  getInteractionSession() {
    if (this.isEnabled && this.analyticsCrossProductInteractionClient) {
      return {
        bridge: this.analyticsCrossProductInteractionClient.getBridge(),
        id: this.analyticsCrossProductInteractionClient.getCurrentInteractionSessionId(),
        prevId: this.analyticsCrossProductInteractionClient.getPreviousInteractionSessionId(),
        source: this.analyticsCrossProductInteractionClient.getInteractionSource()
      };
    } else {
      return {
        bridge: null,
        id: null,
        prevId: null,
        source: null
      };
    }
  }

  /**
   * Validates the cross product interaction session parameters parsed from the URL (if present)
   * If the Base64-encoded URL does not decode into a valid JSON object
   *
   * This method must be called out of the constructor, as the sendEventCallback we expect here
   * depends on crossProductInteractionTracking.getInteractionSession() so the class must be initialised
   */
  validateInteractionSession() {
    if (!this.urlValidation.result) {
      this.sendEventCallback({
        action: 'corrupted',
        actionSubject: 'interactionSession',
        attributes: {
          interactionSessionBlob: this.urlValidation.interactionSessionBlob
        },
        source: 'interactionComponent',
        tags: ['navigationExperiences'] // InteractionSession events will be registered under this platform product
      });
    }
  }
}